import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux'
import {ObjectId} from 'bson'

import { Button, ListTile, TextField, PopOut } from 'components';
import { TagOptions, TagBlock } from 'features';
import { FaEllipsisH, FaPlus } from 'react-icons/fa';
import Options from './Options'

import * as actionCreators from 'store/actions'
import { randomTagColor } from 'utils'
import { Tag } from 'schema'

const Container = styled.div`
    background-color: var(--primary-color);
    border-radius: 5px;
    box-shadow: var(--box-shadow);
    /* border: 1px solid var(--primary-accent-color); */
    cursor: pointer;
    margin: 0.5rem 0 0 0;
    border-style: solid;
    border-width: 2px;
    border-color: ${props => props.error ? 'var(--error-color)' : 'transparent'};
    :hover {
        filter: brightness(var(--brightness));
    }
`

const Wrapper = styled.div`
    width: 100%;
    padding: 0.25rem 0.5rem;
    display: grid;
    grid-template-columns: 1fr auto;
    grid-column-gap: 0.5rem;
    align-items: center;
`

const Label = styled.div`
    font-size: 0.8rem;
    font-weight: bold;
    margin: 0 0 0.2rem 0;
`

const Column = styled.div`
    width: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`

const Row = styled.div`
    display: flex;
    align-items: center;
    margin: ${props => props.styles && props.styles.margin};
    justify-content: ${props => props.styles && props.styles.justifyContent};
`

const Placeholder = styled.div`
    font-size: 0.8rem;
    padding: 0 0.25rem;
    color: ${props => props.error && 'var(--error-color)'};
`

const TextContainer = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    background-color: var(--secondary-color);
    padding: 0.5rem;
    border-radius: 5px;
    overflow: hidden;
    box-shadow: var(--box-shadow);
    margin-bottom: 0.5rem;
`

const TagContainer = styled.div`
    min-height: 1rem;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    overflow: hidden;
`

const Error = styled.div`
    font-size: 0.8rem;
    color: var(--error-color);
    margin-left: 0.2rem;
    :before {
        content: '*'
    }
`

const TagSelector = props => {
    const {
        pageID,
        selected,
        onSelect,
        error
    } = props;
    const [open, setOpen] = useState(false)
    const [width, setWidth] = useState(null)
    const [tag_name, setTagName] = useState(null)
    const ref = React.createRef()
    useEffect(() => {
        setWidth(ref.current.getBoundingClientRect()['width']+5)
    }, [])

    ///////////
    // STORE //
    ///////////
    const dispatch = useDispatch()
    const uid = useSelector((state) => state.auth.uid)
    const user = useSelector((state) => state.auth.user)
    const data = useSelector((state) => state.db.present.data && state.db.present.data.toJS())

    ////////////
    // STYLES //
    ////////////
    const tileStyle = {
        backgroundColor: 'var(--primary-color)'
    }
    const tileOptionStyle = {
        boxShadowColor: 'transparent', 
        color:'var(--primary-accent-color)', 
        backgroundColor:'var(--primary-color)', 
        placement:'bottom', 
        hoverColor:'var(--text-color)', 
        margin:'0rem', padding:'0.2rem', fontSize:'0.7rem'
    }

    ///////////////
    // UTILITIES //
    ///////////////
    const onSelectTag = (tagID) => {
        let updatedTags = selected ? [...selected] : []
        !updatedTags.includes(tagID) && updatedTags.push(tagID)
        onSelect(updatedTags)
    }

    const getTagOptions = () => {
        let tagBlocks = []
        const tags = _.filter(data['tags'], (tag) => tag['pageID'] === pageID) || []
        if(tags.length > 0){
            tags.map(tag => {
                const tagID = tag['tagID']
                tagBlocks.push(
                    <ListTile onSelect={() => onSelectTag(tagID)}  placement='bottom'
                    styles={tileStyle}
                        icon={<TagBlock tag={tag} style={{cursor: 'pointer'}}/>}
                        trailingIcon={<Button icon={<FaEllipsisH/>} contextMenu={<TagOptions tag={tag} tagID={tagID}/>} placement='bottom'
                                        styles={tileOptionStyle}/>}
                        />
                )
            })
        } else {
            tagBlocks.push(<Placeholder>No tags created</Placeholder>)
        }
        return tagBlocks
    }

    const onKeyPress = (event) => {
        if (event.charCode === 13) {
            // console.log(event.target.value)
            createTag(event.target.value)
        } 
    }

    const getOptions = () => {
        const tagBlocks = getTagOptions()
        const textBlock = 
        <TextContainer>
            {getTags(true)}
            <TextField type='single' style={{width: '100%'}} size={1} mantainFocus focus
                value={tag_name}
                onChange={event => setTagName(event.target.value)} 
                onKeyPress={onKeyPress}
                />
        </TextContainer>
        
        let blocks = [textBlock, ...tagBlocks]

        // Placeholder if no pageID
        if(!pageID){
            blocks = [
                <Placeholder>Please select a page first</Placeholder>
            ]
        }
        return <Options width={width} blocks={blocks}/>
    }

    const createTag = () => {
        const {randomColorCode, randomColorName} = randomTagColor(user['theme'] || 'Dark')
        let tag = new Tag({content: tag_name, color: randomColorCode, pageID: pageID, userID: uid})
        let tagID = new ObjectId().toString();
        tag['_id'] = tagID
        dispatch(actionCreators.createTag(tag, data))
        setTagName('') // Reset input

        // Update Editor
        let updatedTags = [...selected]
        updatedTags.push(tagID)
        onSelect(updatedTags)
    }

    const removeTag = tagID => {
        let updatedTags = [...selected]
        updatedTags.splice(updatedTags.indexOf(tagID), 1)
        onSelect(updatedTags)
    }

    const getTags = (onDelete) => {
        const tagBlocks = !selected ? [] : selected.map(tagID => {
            const tags = data['tags']
            const tag = tags[tagID]
            if(tag){ // To filter out tags that got deleted
                const style = {margin: '0.2rem'}
                if(onDelete){
                    return <TagBlock label={tag.content} style={style} tag={tag} onDelete={() => removeTag(tagID)}/>
                } else {
                    return <TagBlock label={tag.content} style={style} tag={tag}/>
                }
            }
        })

        return tagBlocks
    }

    const optionBlock = getOptions()
    return (
        <Container error={error}>
            <PopOut trigger='leftClick' placement='bottom' element={optionBlock} onOpen={() => setOpen(true)} onClose={() => setOpen(false)}>
                <Wrapper ref={ref}>
                    <Column>
                        <Row styles={{margin: '0 0 0.2rem 0'}}>
                            <Label>Tags</Label>
                            {error && <Error>{error}</Error>}
                        </Row>

                        <TagContainer>
                            {getTags()}
                        </TagContainer>
                    </Column>
                    <FaPlus style={{color: 'var(--secondary-color)', fontSize: '0.9rem', fontWeight: 'bold', transform: open && 'scaleY(-1)'}}/>
                </Wrapper>
            </PopOut>
        </Container>
    )
}

export default TagSelector;
